import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Button, Input, PhoneInput, Select, TextArea } from '../../atoms'
import Group from './FormGroup'
import Row from './FormRow'
import Col from './FormCol'

const cleanValidityAfterChange = element => {
  const onChange = () => {
    element.setCustomValidity('')
    element.removeEventListener('change', onChange)
    element.removeEventListener('input', onChange)
  }

  element.addEventListener('change', onChange)
  element.addEventListener('input', onChange)
}

export default function Form({
  className,
  children,
  fieldErrors: fieldErrorsProp,
  onSubmit,
  noAction,
  noValidate,
  ...props
}) {
  const [fields, setFields] = useState([])
  const [fieldErrors, setFieldErrors] = useState([])
  const [invalid, setInvalid] = useState(false)
  const ref = useRef(null)

  const getFormData = () => fields.reduce((a, e) => (e.name ? { ...a, [e.name]: e.value } : a), {})

  const onSubmitClick = useCallback(() => {
    if (!ref.current || noValidate) return
    setInvalid(!ref.current.checkValidity())
  }, [ref, noValidate])

  useEffect(() => {
    if (!ref.current || !noAction) return
    ref.current.action = 'javascript:void(0);'
  }, [ref, noAction])

  useEffect(() => {
    const { current: form } = ref
    if (!form) return
    form && setFields(Array(...form.elements))

    const submitEl = form.querySelector('[type="submit"]')
    if (submitEl) {
      submitEl.addEventListener('click', onSubmitClick)
      return () => submitEl.removeEventListener('click', onSubmitClick)
    }
  }, [children, onSubmitClick])

  useEffect(() => {
    setFieldErrors(
      (fieldErrorsProp || [])
        .map(({ fieldName, errorMessage }) => {
          const field = fields.find(f => f.name === fieldName)
          return field && { field, errorMessage }
        })
        .filter(Boolean)
    )
  }, [fieldErrorsProp, fields])

  useEffect(() => {
    fieldErrors.forEach(({ field, errorMessage }) => {
      field.setCustomValidity(errorMessage)
      field.reportValidity()
      cleanValidityAfterChange(field)
    })

    fieldErrors.length > 0 && setInvalid(true)
  }, [fieldErrors])

  const onSubmitHandler = e => {
    if (onSubmit && onSubmit(e, { fields, data: getFormData() }) === false) {
      return false
    }

    if (!noValidate && !e.target.reportValidity()) {
      setInvalid(true)
    } else {
      setInvalid(false)
    }
  }

  return (
    <form ref={ref} className={clsx(className, invalid && 'invalid')} onSubmit={onSubmitHandler} {...props}>
      {children}
    </form>
  )
}

Form.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  fieldErrors: PropTypes.arrayOf(
    PropTypes.shape({
      fieldName: PropTypes.string,
      errorMessage: PropTypes.string
    })
  ),
  noAction: PropTypes.bool,
  noValidate: PropTypes.bool,
  onSubmit: PropTypes.func
}

Object.assign(Form, {
  Button,
  Col,
  Group,
  Input,
  PhoneInput,
  Row,
  Select,
  TextArea
})
